import React, { memo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import Wrapper from '@components/wrapper.styled';
import getLinkConfig from '@helpers/get-link-config';
import generateId from '@helpers/generate-id';
import Section from '@components/section';
import { navigate } from 'gatsby';
import AnimatedElement from '@components/animated-element';
import {
  StyledTitle,
  ImageContainer,
  Container,
  Block,
  CopyBlock,
  TextContent,
  StyledImage,
  StyledLink,
} from './path-cards.styled';

const pathCardsIdGenerator = generateId();

interface PathCardsItemProps {
  title?: {
    text: string;
  };
  description?: {
    richText: RichTextBlock[];
  };
  image?: ImageProps;
  link?: {
    uid?: string;
    url?: string;
  };
  link_label?: string;
}

interface PathCardsProps {
  title?: string;
  items: PathCardsItemProps[];
}

const PathCardBlock = memo(({ item }: { item: PathCardsItemProps }) => {
  const linkConfig = getLinkConfig(item.link);

  const handleClick = async () => {
    if (linkConfig.url) {
      try {
        await navigate(linkConfig.url);
      } catch (error) {
        console.error('Navigation failed:', error);
        // Fallback to regular link if navigation fails
        window.location.href = linkConfig.url;
      }
    }
  };

  return (
    <Block hasLink={!!linkConfig.url} onClick={handleClick}>
      {(item.image?.gatsbyImageData || item.image?.url) && (
        <ImageContainer>
          <StyledImage
            gatsbyImageData={item.image.gatsbyImageData}
            url={item.image.url}
            alt={item.image.alt || ''}
            loading="lazy"
          />
        </ImageContainer>
      )}
      <TextContent>
        <CopyBlock>
          {item.title?.text && (
            <Text
              type="title"
              tag="h3"
              stringText={item.title?.text}
              fontSize="heading5"
              fontColor="white"
              margin="none"
            />
          )}
          {item.description?.richText && (
            <Text
              type="description"
              fontColor="white"
              richText={item.description?.richText}
              margin="md"
            />
          )}
        </CopyBlock>
        {linkConfig.url && (
          <StyledLink
            title={item.link_label || 'Explore'}
            url={linkConfig.url}
            isExternal={linkConfig.isExternal}
            displayArrow={false}
          />
        )}
      </TextContent>
    </Block>
  );
});

PathCardBlock.displayName = 'PathCardBlock';

const PathCards = ({ title, items }: PathCardsProps): JSX.Element => (
  <Section backgroundColor="white">
    <Wrapper>
      {!!title && <StyledTitle type="title" tag="h2" fontSize="heading2" stringText={title} />}
      <Container>
        {items.map((item, index) => (
          <AnimatedElement
            key={pathCardsIdGenerator.next().value}
            animationType="slideInLeft"
            delay={index * 0.125}
            threshold={0.1}
            rootMargin="50px"
          >
            <PathCardBlock item={item} />
          </AnimatedElement>
        ))}
      </Container>
    </Wrapper>
  </Section>
);

export default PathCards;
